// ABOUTME: Content block renderer for SDK message content.
// ABOUTME: Renders text, thinking, tool use, tool result, and structured response blocks.

import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Wrench, AlertCircle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  ContentBlock,
  TextBlock,
  ThinkingBlock,
  ToolUseBlock,
  ToolResultBlock,
  StructuredResponseBlock,
  ParseErrorBlock,
  SmartAction,
} from '@/types/sdk';
import { CodeBlock } from './CodeBlock';
import { InsightRenderer, ParseErrorDisplay, TextResponseDisplay } from './InsightRenderer';

interface ContentBlockRendererProps {
  block: ContentBlock;
  toolResults?: Map<string, ToolResultBlock>;
  onSmartAction?: (action: SmartAction) => void;
  showSmartActions?: boolean;
}

export function ContentBlockRenderer({ block, toolResults, onSmartAction, showSmartActions }: ContentBlockRendererProps) {
  switch (block.type) {
    case 'text':
      return <TextBlockView block={block} showSmartActions={showSmartActions} onSmartAction={onSmartAction} />;
    case 'thinking':
      return <ThinkingBlockView block={block} />;
    case 'tool_use':
      return <ToolUseBlockView block={block} toolResult={toolResults?.get(block.id)} />;
    case 'tool_result':
      return <ToolResultBlockView block={block} />;
    case 'structured_response':
      return <StructuredResponseBlockView block={block} onSmartAction={onSmartAction} showSmartActions={showSmartActions} />;
    case 'parse_error':
      return <ParseErrorBlockView block={block} />;
    default:
      return null;
  }
}

// Structured response block - renders either text or insight response
function StructuredResponseBlockView({
  block,
  onSmartAction,
  showSmartActions,
}: {
  block: StructuredResponseBlock;
  onSmartAction?: (action: SmartAction) => void;
  showSmartActions?: boolean;
}) {
  if (block.response_type === 'text') {
    // Text response for casual/meta questions
    const content = (block.content as { type: 'text'; content: string }).content;
    return <TextResponseDisplay content={content} />;
  } else if (block.response_type === 'insight') {
    // Insight response for data questions
    const insightContent = block.content as { type: 'insight'; insight: any };
    return <InsightRenderer insight={insightContent.insight} onSmartAction={onSmartAction} showSmartActions={showSmartActions} />;
  }
  return null;
}

// Parse error block - shows error when structured response parsing fails
function ParseErrorBlockView({ block }: { block: ParseErrorBlock }) {
  return (
    <ParseErrorDisplay
      errorMessage={block.error_message}
      rawContent={block.raw_content}
      details={block.details}
    />
  );
}

// Try to parse text as structured JSON response (fallback for when backend doesn't parse)
// Handles both raw JSON and JSON embedded in markdown code blocks
// Exported for use in AgentExecutionView to detect structured content in text blocks
export function tryParseAsStructuredResponse(text: string): { type: 'text' | 'insight'; content: any } | null {
  const trimmed = text.trim();

  // First, try to extract JSON from markdown code blocks: ```json ... ``` or ``` ... ```
  const codeBlockMatch = trimmed.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
  let jsonCandidate = codeBlockMatch ? codeBlockMatch[1].trim() : null;

  // If we found a code block with JSON-like content, try to parse it
  if (jsonCandidate && jsonCandidate.startsWith('{') && jsonCandidate.includes('"type"')) {
    try {
      const parsed = JSON.parse(jsonCandidate);
      if (parsed.type === 'text' && typeof parsed.content === 'string') {
        return { type: 'text', content: parsed.content };
      }
      if (parsed.type === 'insight' && parsed.insight) {
        return { type: 'insight', content: parsed.insight };
      }
    } catch {
      // Code block content wasn't valid JSON
    }
  }

  // If no code block, check if the entire text is JSON
  if (trimmed.startsWith('{') && trimmed.includes('"type"')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed.type === 'text' && typeof parsed.content === 'string') {
        return { type: 'text', content: parsed.content };
      }
      if (parsed.type === 'insight' && parsed.insight) {
        return { type: 'insight', content: parsed.insight };
      }
    } catch {
      // Not valid JSON
    }
  }

  return null;
}

// Text block - renders markdown content OR structured response if JSON detected
function TextBlockView({ block, showSmartActions, onSmartAction }: { block: TextBlock; showSmartActions?: boolean; onSmartAction?: (action: SmartAction) => void }) {
  // Check if this text block is actually a JSON structured response
  const structuredContent = tryParseAsStructuredResponse(block.text);

  if (structuredContent) {
    if (structuredContent.type === 'text') {
      return <TextResponseDisplay content={structuredContent.content} />;
    } else if (structuredContent.type === 'insight') {
      return <InsightRenderer insight={structuredContent.content} showSmartActions={showSmartActions} onSmartAction={onSmartAction} />;
    }
  }

  // Regular markdown text
  return (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <p className="text-surface-600 leading-relaxed mb-3 last:mb-0">
              {children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="text-surface-800 font-semibold">{children}</strong>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside space-y-1 text-surface-600 mb-3">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside space-y-1 text-surface-600 mb-3">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="text-surface-600">{children}</li>,
          code: ({ className, children }) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !match;
            if (isInline) {
              return (
                <code className="px-1.5 py-0.5 rounded-sm bg-surface-100 text-primary-600 font-mono text-xs">
                  {children}
                </code>
              );
            }
            return (
              <CodeBlock
                code={String(children).replace(/\n$/, '')}
                language={match[1] as 'sql' | 'python' | 'json'}
              />
            );
          },
          pre: ({ children }) => <>{children}</>,
        }}
      >
        {block.text}
      </ReactMarkdown>
    </div>
  );
}

// Thinking block - expandable agent reasoning
function ThinkingBlockView({ block }: { block: ThinkingBlock }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="rounded-sm bg-primary-50 dark:bg-primary-100 border border-primary-200 dark:border-primary-300 overflow-hidden"
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-primary-100 dark:hover:bg-primary-200 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2 text-primary-600">
          <Brain className="w-4 h-4" />
          <span className="text-sm font-medium">Agent Reasoning</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-surface-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-surface-500" />
        )}
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 pb-4"
          >
            <p className="text-sm text-surface-600 italic leading-relaxed">
              {block.thinking}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Tool use block - shows the tool being called
function ToolUseBlockView({
  block,
  toolResult,
}: {
  block: ToolUseBlock;
  toolResult?: ToolResultBlock;
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const isCompleted = !!toolResult;
  const isError = toolResult?.is_error;

  // Determine what kind of tool this is
  const toolInfo = getToolInfo(block.name, block.input);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-sm bg-surface-50 border border-surface-200 overflow-hidden"
    >
      {/* Tool header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-100 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div
            className={`
              w-6 h-6 rounded-sm flex items-center justify-center shrink-0
              ${isError
                ? 'bg-error-light text-error'
                : isCompleted
                ? 'bg-success-light text-success'
                : 'bg-primary-50 text-primary-600'}
            `}
          >
            {isError ? (
              <AlertCircle className="w-3.5 h-3.5" />
            ) : isCompleted ? (
              <CheckCircle className="w-3.5 h-3.5" />
            ) : (
              <Wrench className="w-3.5 h-3.5 animate-pulse" />
            )}
          </div>
          <div className="text-left min-w-0 flex-1">
            <span className="text-sm font-medium text-surface-800 shrink-0">
              {toolInfo.label}
            </span>
            {!isExpanded && toolInfo.preview && (
              <span className="ml-2 text-xs text-surface-500 truncate inline-block max-w-[300px] align-middle">
                {toolInfo.preview}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-surface-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-surface-500" />
          )}
        </div>
      </button>

      {/* Tool input */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            {/* Show input (SQL query, Python code, etc.) */}
            {toolInfo.code && (
              <div className="px-4 pb-4">
                <CodeBlock
                  code={toolInfo.code}
                  language={toolInfo.language}
                  title={toolInfo.codeTitle}
                />
              </div>
            )}

            {/* For tools without code (like generate_insight), show preview info */}
            {!toolInfo.code && toolInfo.preview && (
              <div className="px-4 pb-4">
                <div className="rounded-sm bg-primary-50 border-l-2 border-primary-300 px-4 py-3">
                  <p className="text-sm text-surface-600 italic leading-relaxed">
                    "{toolInfo.preview}"
                  </p>
                </div>
              </div>
            )}

            {/* Show result only for SQL and Python tools (not schema discovery, cost estimation, etc.) */}
            {toolResult && toolInfo.showResult && <ToolResultBlockView block={toolResult} inline />}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Tool result block - shows tool execution output with expandable data preview
function ToolResultBlockView({
  block,
  inline = false,
}: {
  block: ToolResultBlock;
  inline?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Parse content to check if it's tabular data
  const parsedContent = parseToolResultContent(block.content);
  const isTabularData = parsedContent.isTabular;
  const rowCount = parsedContent.rowCount;
  const columnCount = parsedContent.columnCount;
  const previewRows = parsedContent.previewRows;

  // Don't show anything if no rows returned (unless it's an error)
  if (!block.is_error && (!isTabularData || rowCount === 0)) {
    return null;
  }

  if (block.is_error) {
    // Extract error message - handle both string and structured content
    let errorMessage = '';
    if (typeof block.content === 'string') {
      errorMessage = block.content;
    } else if (Array.isArray(block.content)) {
      // Extract text from content blocks
      errorMessage = block.content
        .filter((item): item is { type: string; text: string } =>
          typeof item === 'object' && item !== null && 'text' in item
        )
        .map(item => item.text)
        .join('\n');
    } else {
      errorMessage = JSON.stringify(block.content);
    }

    // Clean up common error prefixes for cleaner display
    errorMessage = errorMessage
      .replace(/^Error:\s*/i, '')
      .replace(/^SQL Error:\s*/i, '')
      .trim();

    return (
      <div className={`${inline ? 'px-4 pb-4' : ''}`}>
        <div className="flex items-center gap-1.5 py-1.5 text-error/70 dark:text-error">
          <AlertCircle className="w-3 h-3 dark:w-3.5 dark:h-3.5" />
          <span className="text-[11px] dark:text-xs font-medium">Error</span>
        </div>
        <div className="mt-1 rounded-sm overflow-hidden bg-error-light/30 dark:bg-error-light/50 border border-error/10 dark:border-error/20">
          <pre className="p-3 text-[11px] text-surface-600 dark:text-surface-500 font-mono whitespace-pre-wrap overflow-x-auto max-h-24 overflow-y-auto">
            {errorMessage}
          </pre>
        </div>
      </div>
    );
  }

  // For tabular data (SQL results), show collapsible preview
  if (isTabularData && rowCount > 0) {
    return (
      <div className={`${inline ? 'px-4 pb-4' : ''}`}>
        {/* Minimal result indicator for light mode, subtle box for dark mode */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between py-1.5 text-surface-400 hover:text-surface-500 transition-colors cursor-pointer dark:bg-surface-50 dark:border dark:border-surface-200 dark:rounded-sm dark:px-3 dark:py-2"
        >
          <div className="flex items-center gap-1.5">
            <CheckCircle className="w-3 h-3 text-success/60 dark:text-success dark:w-3.5 dark:h-3.5" />
            <span className="text-[11px] dark:text-xs dark:font-medium dark:text-surface-600">
              {rowCount} {rowCount === 1 ? 'row' : 'rows'}
            </span>
            {columnCount > 0 && (
              <span className="text-[11px] text-surface-300 dark:text-xs dark:text-surface-400">
                · {columnCount} {columnCount === 1 ? 'col' : 'cols'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 dark:gap-1.5">
            <span className="text-[10px]">
              {isExpanded ? 'Hide' : 'Preview'}
            </span>
            {isExpanded ? (
              <ChevronUp className="w-3 h-3 dark:w-3.5 dark:h-3.5" />
            ) : (
              <ChevronDown className="w-3 h-3 dark:w-3.5 dark:h-3.5" />
            )}
          </div>
        </button>

        {/* Expandable data preview */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-2 dark:mt-0 dark:border-t dark:border-surface-200"
            >
              <div className="rounded-sm overflow-hidden bg-surface-100 border border-surface-200 dark:border-0 dark:rounded-none dark:bg-surface-0">
                <div className="p-3 overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="border-b border-surface-200 dark:border-surface-100">
                        {previewRows.length > 0 && Object.keys(previewRows[0]).map((col) => (
                          <th key={col} className="text-left py-1.5 px-2 text-surface-500 font-medium font-mono">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, idx) => (
                        <tr key={idx} className="border-b border-surface-200/50 dark:border-surface-50 last:border-0">
                          {Object.values(row).map((val, colIdx) => (
                            <td key={colIdx} className="py-1.5 px-2 text-surface-600 font-mono">
                              {formatCellValue(val)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rowCount > previewRows.length && (
                    <div className="text-[10px] text-surface-400 mt-2 text-center">
                      Showing {previewRows.length} of {rowCount}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Fallback for non-tabular results
  const formattedContent = formatToolResult(block.content);
  return (
    <div className={`${inline ? 'px-4 pb-4' : ''}`}>
      <div className="flex items-center gap-1.5 py-1.5 text-surface-400 dark:bg-surface-50 dark:border dark:border-surface-200 dark:rounded-sm dark:px-3 dark:py-2">
        <CheckCircle className="w-3 h-3 text-success/60 dark:text-success dark:w-3.5 dark:h-3.5" />
        <span className="text-[11px] dark:text-xs dark:font-medium dark:text-surface-600">Result</span>
      </div>
      <div className="mt-2 dark:mt-0 rounded-sm overflow-hidden bg-surface-100 border border-surface-200 dark:border-0 dark:border-t dark:rounded-none dark:bg-surface-0">
        <pre className="p-3 text-[11px] text-surface-500 font-mono whitespace-pre-wrap overflow-x-auto max-h-32 overflow-y-auto">
          {formattedContent}
        </pre>
      </div>
    </div>
  );
}

// Helper to parse tool result content and extract tabular data info
function parseToolResultContent(content: string | Record<string, unknown>[] | null): {
  isTabular: boolean;
  rowCount: number;
  columnCount: number;
  previewRows: Record<string, unknown>[];
} {
  if (content === null) {
    return { isTabular: false, rowCount: 0, columnCount: 0, previewRows: [] };
  }

  // If it's a string, try to parse as JSON
  let data: unknown = content;
  if (typeof content === 'string') {
    try {
      data = JSON.parse(content);
    } catch {
      return { isTabular: false, rowCount: 0, columnCount: 0, previewRows: [] };
    }
  }

  // Handle SDK content block format: array of { type: 'text', text: '...' }
  // This is how tool results come from the SDK
  if (Array.isArray(data) && data.length > 0) {
    const firstItem = data[0] as Record<string, unknown>;
    if (firstItem.type === 'text' && typeof firstItem.text === 'string') {
      // Extract text blocks and find one that contains JSON array data
      const textBlocks = (data as Array<{ type: string; text: string }>)
        .filter(item => item.type === 'text')
        .map(item => item.text);

      // Try each text block to find valid JSON array (the actual data)
      for (const text of textBlocks) {
        const trimmed = text.trim();
        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
          try {
            data = JSON.parse(trimmed);
            break;
          } catch {
            // Continue to next block
          }
        }
      }

      // If data is still the original array of text blocks, parsing failed
      if (Array.isArray(data) && (data[0] as Record<string, unknown>)?.type === 'text') {
        return { isTabular: false, rowCount: 0, columnCount: 0, previewRows: [] };
      }
    }
  }

  // Check if it's an object with a 'data' array (common API response format)
  if (typeof data === 'object' && !Array.isArray(data) && data !== null) {
    const dataObj = data as Record<string, unknown>;
    if (Array.isArray(dataObj.data)) {
      data = dataObj.data;
    }
  }

  // Check if it's an array of objects (tabular data)
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null) {
    const rows = data as Record<string, unknown>[];
    const columns = Object.keys(rows[0]);
    return {
      isTabular: true,
      rowCount: rows.length,
      columnCount: columns.length,
      previewRows: rows.slice(0, 5), // Show first 5 rows as preview
    };
  }

  return { isTabular: false, rowCount: 0, columnCount: 0, previewRows: [] };
}

// Helper to format cell values for display
function formatCellValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'number') {
    // Format numbers nicely
    if (Number.isInteger(val)) return val.toLocaleString();
    return val.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  if (typeof val === 'object') return JSON.stringify(val);
  const str = String(val);
  return str.length > 50 ? str.substring(0, 47) + '...' : str;
}

// Helper to filter out rationale and other meta fields from tool input for display
function filterToolInput(input: Record<string, unknown>): Record<string, unknown> {
  // Remove fields that are meta/config and not part of the actual tool input
  const { rationale, use_cache, row_limit, ...rest } = input;
  return rest;
}

// Helper to normalize MCP tool names (remove mcp__*__ prefix)
function normalizeToolName(name: string): string {
  // Handle MCP prefixed names like mcp__obsidian__execute_sql or mcp__tools__execute_sql
  const mcpMatch = name.match(/^mcp__[^_]+__(.+)$/);
  return mcpMatch ? mcpMatch[1] : name;
}

// Helper to get tool display info
function getToolInfo(name: string, input: Record<string, unknown>) {
  const filteredInput = filterToolInput(input);
  const normalizedName = normalizeToolName(name);

  switch (normalizedName) {
    case 'execute_sql':
    case 'sql_query':
      return {
        label: 'SQL Query',
        code: input.sql as string,
        language: 'sql' as const,
        codeTitle: 'SQL',
        preview: truncate((input.sql as string) || '', 50),
        isSqlTool: true,
        showResult: true, // Show SQL results
      };
    case 'execute_python':
    case 'python_execution':
      return {
        label: 'Python Execution',
        code: input.code as string,
        language: 'python' as const,
        codeTitle: 'Python',
        preview: truncate((input.code as string) || '', 50),
        isSqlTool: false,
        showResult: false, // Don't show Python results
      };
    case 'generate_chart':
      return {
        label: 'Generate Chart',
        code: JSON.stringify(filteredInput, null, 2),
        language: 'json' as const,
        codeTitle: 'Chart Config',
        preview: (input.type as string) || 'chart',
        isSqlTool: false,
        showResult: false,
      };
    case 'generate_python_visualization':
    case 'python_viz':
      return {
        label: 'Python Visualization',
        code: input.code as string,
        language: 'python' as const,
        codeTitle: 'Python',
        preview: (input.viz_type as string) || 'visualization',
        isSqlTool: false,
        showResult: false, // Don't show Python viz results
      };
    case 'analyze_schema':
    case 'schema_discovery':
    case 'discover_schema':
      return {
        label: 'Schema Discovery',
        code: JSON.stringify(filteredInput, null, 2),
        language: 'json' as const,
        codeTitle: 'Query',
        preview: 'Analyzing database schema',
        isSqlTool: false,
        showResult: false, // Don't show schema discovery results
      };
    case 'generate_insight':
      // For generate_insight, show the rationale as the preview
      const insightRationale = (input.rationale as string) || '';
      const insightTitle = (input.insight as Record<string, unknown>)?.title as string || '';
      return {
        label: 'Creating Insight',
        code: null, // Don't show the full JSON, it's verbose
        language: 'json' as const,
        codeTitle: 'Insight',
        preview: insightRationale || insightTitle || 'Generating response',
        isSqlTool: false,
        showResult: false, // Don't show insight tool results
      };
    default:
      return {
        label: normalizedName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        code: JSON.stringify(filteredInput, null, 2),
        language: 'json' as const,
        codeTitle: 'Input',
        preview: null,
        isSqlTool: false,
        showResult: false, // Don't show results for unknown tools
      };
  }
}

// Helper to format tool result content
function formatToolResult(content: string | Record<string, unknown>[] | null): string {
  if (content === null) return 'No result';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    // Format as table-like structure for readability
    if (content.length === 0) return 'Empty result set';
    return JSON.stringify(content, null, 2);
  }
  return JSON.stringify(content, null, 2);
}

// Helper to truncate strings
function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.substring(0, length) + '...';
}
